import { type ReactNode, useState } from "react";
import { cn, initialsOf } from "./utils";

export type AvatarTone = "blue" | "sky" | "sage" | "rose" | "sand" | "moss" | "stone" | "self";

const tones: Record<AvatarTone, { soft: string; solid: string }> = {
  blue: { soft: "bg-tone-blue text-tone-blue-ink", solid: "bg-tone-blue-ink text-linen" },
  sky: { soft: "bg-tone-sky text-tone-sky-ink", solid: "bg-tone-sky-ink text-linen" },
  sage: { soft: "bg-tone-sage text-tone-sage-ink", solid: "bg-tone-sage-ink text-linen" },
  rose: { soft: "bg-tone-rose text-tone-rose-ink", solid: "bg-tone-rose-ink text-linen" },
  sand: { soft: "bg-tone-sand text-tone-sand-ink", solid: "bg-tone-sand-ink text-linen" },
  moss: { soft: "bg-tone-moss text-tone-moss-ink", solid: "bg-tone-moss-ink text-linen" },
  stone: { soft: "bg-tone-stone text-tone-stone-ink", solid: "bg-tone-stone-ink text-linen" },
  // Your own avatar is always gold.
  self: { soft: "bg-gold text-ink", solid: "bg-gold text-ink" },
};

// Text colour that reads well on each tone's pale ground, for sender names in groups.
export const toneInk: Record<AvatarTone, string> = {
  blue: "text-tone-blue-ink",
  sky: "text-tone-sky-ink",
  sage: "text-tone-sage-ink",
  rose: "text-tone-rose-ink",
  sand: "text-tone-sand-ink",
  moss: "text-tone-moss-ink",
  stone: "text-tone-stone-ink",
  self: "text-gold-ink",
};

export type AvatarProps = {
  name: string;
  initials?: string;
  tone?: AvatarTone;
  size?: number;
  shape?: "circle" | "rounded";
  /** Solid fill with linen text, used on call tiles. */
  solid?: boolean;
  serif?: boolean;
  online?: boolean;
  /** Colour of the ring around the presence dot. Match the surface the avatar sits on. */
  ringClassName?: string;
  icon?: ReactNode;
  /** The person's photo. Initials show while it loads, and if it cannot load. */
  src?: string | null;
  /** Set when the avatar is the only thing naming the person. Decorative otherwise. */
  label?: string;
  className?: string;
};

export function Avatar({
  name,
  initials,
  tone = "stone",
  size = 52,
  shape = "circle",
  solid = false,
  serif = false,
  online = false,
  ringClassName = "border-linen",
  icon,
  src,
  label,
  className,
}: AvatarProps) {
  const [broken, setBroken] = useState<string | null>(null);
  const photo = src && broken !== src ? src : null;
  const radius = shape === "circle" ? "50%" : Math.round(size * 0.3);
  const dot = Math.min(22, Math.max(10, Math.round(size * 0.28)));
  const dotBorder = size >= 80 ? 4 : 3;
  const palette = tones[tone];
  return (
    <span
      className={cn("relative inline-flex flex-none", className)}
      style={{ width: size, height: size }}
      {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}
    >
      <span
        className={cn(
          "flex h-full w-full items-center justify-center font-bold leading-none",
          serif && "font-serif font-semibold",
          solid ? palette.solid : palette.soft,
        )}
        style={{ borderRadius: radius, fontSize: Math.round(size * (serif ? 0.37 : 0.33)) }}
      >
        {icon ?? initials ?? initialsOf(name)}
      </span>
      {photo && (
        <img
          src={photo}
          alt=""
          draggable={false}
          onError={() => setBroken(photo)}
          className="absolute inset-0 h-full w-full object-cover"
          style={{ borderRadius: radius }}
        />
      )}
      {online && (
        <span
          className={cn("absolute rounded-full bg-olive box-border", ringClassName)}
          style={{
            width: dot,
            height: dot,
            right: size >= 80 ? 6 : 0,
            bottom: size >= 80 ? 6 : 0,
            borderWidth: dotBorder,
            borderStyle: "solid",
          }}
        />
      )}
    </span>
  );
}

export type Product = "queravel" | "malak" | "penielly";

// Each app's own icon, as it ships on the Mac and on phones, drawn on the same 64-unit square.
const marks: Record<Product, { name: string; draw: ReactNode }> = {
  queravel: {
    name: "Queravel",
    draw: (
      <>
        <rect width="64" height="64" rx="14" fill="#1B4D3E" />
        <circle cx="29.25" cy="28.75" r="11.25" fill="none" stroke="#FBFAF6" strokeWidth="5.1" />
        <path d="M35.9 35.4 46.4 45.9" stroke="#52B788" strokeWidth="5.1" strokeLinecap="round" />
      </>
    ),
  },
  malak: {
    name: "Malakh",
    draw: (
      <>
        <rect width="64" height="64" rx="14" fill="#FBBF05" />
        <g
          transform="scale(1.4545) skewX(-12) translate(5.5 0)"
          fill="none"
          stroke="#000"
          strokeWidth="3.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M11 31V22a4.6 4.6 0 0 1 9.2 0V31M20.2 22.5a4.6 4.6 0 0 1 9.2 0V31c0 2.6 2 3.4 3.6 2.6" />
        </g>
      </>
    ),
  },
  penielly: {
    name: "Penielly",
    draw: (
      <>
        <rect width="64" height="64" rx="14" fill="#1E5C46" />
        <text
          x="32"
          y="46"
          fontFamily="Georgia, 'Times New Roman', serif"
          fontSize="42"
          fontWeight="700"
          fill="#FBFAF8"
          textAnchor="middle"
        >
          P
        </text>
      </>
    ),
  },
};

export type ProductMarkProps = {
  product: Product;
  size?: number;
  label?: string;
  className?: string;
};

/** The logo of a SiliconBlast app that Rhema links to. */
export function ProductMark({ product, size = 48, label, className }: ProductMarkProps) {
  const mark = marks[product];
  return (
    // biome-ignore lint/a11y/noSvgWithoutTitle: a labelled mark is named by aria-label, and an unlabelled one is hidden
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={cn("inline-block flex-none", className)}
      {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}
    >
      {mark.draw}
    </svg>
  );
}

export const productName = (product: Product) => marks[product].name;
